import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { CheckCircle2, Flame, Phone } from 'lucide-react';

const BrandschutzhelferPage = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO page="brandschutz" />
      <Navbar />
      <main className="flex-1">
        
        {/* MOBILE: Klassischer Hero wie Dortmund (Bild oben, Titel darunter) */}
        <div className="md:hidden">
          {/* Vollbreites Bild */}
          <div className="w-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/f1l9fro3_EC2E7AC4-FD87-481C-BF8C-26356ADB42A8.png"
              alt="Brandschutzhelfer-Ausbildung"
              className="w-full h-auto object-cover"
              style={{ aspectRatio: '16/9' }}
            />
          </div>
          
          {/* Titel-Bereich unter dem Bild */}
          <div className="bg-white px-4 py-6">
            <h1 className="text-2xl font-bold text-[#1e3a5f] leading-tight mb-3">
              Brandschutzhelfer-Ausbildung
            </h1>
            <p className="text-slate-600 text-base leading-relaxed">
              Praxisnahe Ausbildung für Ihre Mitarbeitenden – inklusive Feuerlöschübungen.
            </p>
          </div>
        </div>

        {/* DESKTOP: Bestehender Hero mit Gradient-Overlay (unverändert) */}
        <div className="hidden md:block">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div 
              className="relative overflow-hidden rounded-xl"
              style={{ 
                backgroundImage: `url("https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/f1l9fro3_EC2E7AC4-FD87-481C-BF8C-26356ADB42A8.png")`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'center center'
              }}
            >
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 via-[#1e3a5f]/70 to-[#1e3a5f]/20" />
              
              {/* Content */}
              <div className="relative px-8 lg:px-10 py-12 lg:py-14">
                <div className="flex flex-row items-center justify-between gap-6 min-h-[240px]">
                  {/* Titel + Zusatzinfo - Links */}
                  <div className="max-w-xl">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white rounded-full text-sm font-semibold shadow-sm">
                        <Flame className="w-3.5 h-3.5" />
                        Brandschutz-Ausbildung
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 text-[#1e3a5f] rounded-full text-sm font-semibold shadow-sm">
                        Praxisnah & anerkannt
                      </span>
                    </div>
                    
                    <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm">
                      Brandschutzhelfer-Ausbildung
                    </h1>
                    
                    <p className="text-white/90 text-lg mt-3 drop-shadow-sm">
                      Praxisnahe Ausbildung für Ihre Mitarbeitenden – inklusive Feuerlöschübungen.
                    </p>
                    
                    {/* Zusätzlicher Teaser-Text */}
                    <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                      <p className="text-white font-semibold text-lg">
                        Interesse an einer Brandschutzhelfer-Ausbildung?
                      </p>
                      <p className="text-white/80 text-sm mt-1">
                        Kontaktieren Sie uns für ein individuelles Angebot für Ihren Betrieb.
                      </p>
                    </div>
                  </div>
                  
                  {/* CTA-Box - Rechts */}
                  <div className="flex-shrink-0 flex items-center">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <Phone className="w-6 h-6 text-orange-600" />
                        <span className="font-semibold text-[#1e3a5f]">Jetzt anfragen</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-4">Wir erstellen Ihnen gerne ein individuelles Angebot.</p>
                      <a href="tel:020419878666">
                        <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                          02041 9878 666
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* INHALT - Same container width as navbar */}
        <section className="py-12 sm:py-16">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
              
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mt-8 mb-4">Brandschutzhelfer-Ausbildung nach aktuellen Vorgaben</h2>
              
              <p>
                Brände stellen in Betrieben und Einrichtungen ein erhebliches Risiko dar. Eine fundierte 
                Ausbildung von Brandschutzhelfern ist daher ein wichtiger Bestandteil des betrieblichen 
                Arbeitsschutzes.
              </p>
              
              <p>
                Wir bieten praxisnahe Brandschutzhelfer-Ausbildungen an, die Mitarbeitende auf den Ernstfall 
                vorbereiten und Sicherheit im Umgang mit Brandgefahren vermitteln.
              </p>
              
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mt-8 mb-4">Inhalte der Ausbildung</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Grundlagen des vorbeugenden Brandschutzes</li>
                <li>Brandentstehung und Brandklassen</li>
                <li>Richtiges Verhalten im Brandfall</li>
                <li>Alarmierung und Evakuierung</li>
                <li>Praktische Feuerlöschübungen</li>
                <li>Umgang mit Feuerlöschern und Löschmitteln</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mt-8 mb-4">Für wen ist die Ausbildung geeignet?</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Unternehmen und Betriebe aller Größen</li>
                <li>Verwaltungen, Schulen und soziale Einrichtungen</li>
                <li>Verantwortliche für Arbeitssicherheit</li>
                <li>Mitarbeitende, die als Brandschutzhelfer benannt werden sollen</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mt-8 mb-4">Ihr Vorteil</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Praxisorientierte Schulung</li>
                <li>Verständliche Vermittlung</li>
                <li>Durchführung durch erfahrene Ausbilder</li>
                <li>Schulung direkt vor Ort oder nach Absprache</li>
              </ul>
              
              <p className="mt-8 text-lg font-medium text-[#1e3a5f]">
                Jetzt unverbindlich anfragen und Brandschutzhelfer ausbilden lassen.
              </p>
            </div>
            
            <div className="mt-12">
              <Link to="/kontakt">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-8 h-12 text-base font-semibold">
                  Jetzt anfragen
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default BrandschutzhelferPage;
